/**
 * What each transport will carry, per attachment kind — the single source of
 * truth for the composer's enabled/disabled controls AND for the server-side
 * re-check before dispatch, so the two can't drift.
 *
 * The rule is that an unsupported control is rendered and visibly disabled with
 * the reason, never hidden: a host who can't see the button learns nothing,
 * while a host who taps a greyed-out one learns which channel to switch to.
 *
 * Limits come from the providers, not from us:
 *  - WhatsApp (via Twilio) caps a free-form image at 5 MB and everything else
 *    at 16 MB, and only accepts free-form media inside the 24-hour service
 *    window that the guest's own last message opens.
 *  - SMS carries no media at all. MMS does, but only on US/Canada carriers —
 *    which is why an Italian or Slovenian property sees the SMS row disabled.
 *  - Resend attaches up to 10 MB; above that we send a link instead.
 *  - Booking.com and Airbnb expose no media API we have verified, so they are
 *    disabled rather than promised.
 *  - Staff-to-staff never leaves our own stack, so its limits are ours to pick;
 *    they match WhatsApp's so a host learns one set of numbers, not two.
 */

import type { GuestMessageTransport, MediaKind } from "./enums";

/** Attachment kinds, matching `media_kind` on both attachment tables. */
export type AttachmentKind = MediaKind;

/** A guest transport, or `staff` for a staff-to-staff chat. */
export type AttachmentTransport = GuestMessageTransport | "staff";

export interface AttachmentCapabilityContext {
  /** Size of the file in hand, when there is one. Drives the oversize reason. */
  sizeBytes?: number;
  /**
   * True when the recipient's carrier region supports MMS — US and Canada only.
   * Everywhere else SMS is text-only and an attachment errors with 21612.
   */
  smsSupportsMms?: boolean;
  /**
   * True when the guest has messaged within WhatsApp's 24-hour service window,
   * which is what free-form (non-template) media requires. Unknown/undefined is
   * treated as OUTSIDE the window: assuming it is open would render an enabled
   * button that then fails at Meta.
   */
  withinServiceWindow?: boolean;
}

/**
 * Why a transport refuses an attachment, as a code each surface words itself:
 * the host app from its catalog, the server with
 * {@link describeAttachmentRefusal}.
 */
export type AttachmentRefusal =
  | {
      code:
        | "outside_service_window"
        | "outside_service_window_voice"
        | "sms_no_media"
        | "unsupported_channel";
    }
  | { code: "oversize"; maxBytes: number; sizeBytes: number };

/** Whether `kind` can be sent on a transport right now, and if not, why. */
export type AttachmentCapability =
  | { enabled: true; maxBytes: number; reason: null; requiresTemplate: false }
  | {
      enabled: false;
      /** The transport's ceiling for this kind, or null when it carries none. */
      maxBytes: number | null;
      reason: AttachmentRefusal;
      /**
       * True when the transport could carry this kind, but only inside a
       * pre-approved media-header template — which is a Meta approval process,
       * not something the composer can do. Callers surface it as "send a
       * template to reopen the conversation" rather than as a plain refusal.
       */
      requiresTemplate: boolean;
    };

const MB = 1024 * 1024;

/** `5.0` reads like a machine wrote it; `5` is what a host would say. */
const TRAILING_ZERO_DECIMAL = /\.0$/;

/** Above this, Resend rejects the attachment and we send a link instead. */
export const EMAIL_MAX_ATTACHMENT_BYTES = 10 * MB;

const WHATSAPP_MAX_BYTES: Record<AttachmentKind, number> = {
  file: 16 * MB,
  image: 5 * MB,
  video: 16 * MB,
  voice: 16 * MB,
};

// MMS is capped at 5 MB by the carriers Twilio fronts. Twilio's own guidance is
// to stay under 600 KB for reliable delivery, but that is advice about how well
// it renders, not a limit the API enforces, so the ceiling is the hard one.
//
// Nothing computes carrier region yet, so `smsSupportsMms` is never set and
// every SMS conversation takes the disabled branch. That is the correct answer
// for the Italian and Slovenian properties this serves; the ceiling is here so
// enabling MMS is a matter of supplying the flag, not of rewriting the matrix.
const MMS_MAX_BYTES = 5 * MB;

const STAFF_MAX_BYTES: Record<AttachmentKind, number> = {
  file: 16 * MB,
  image: 5 * MB,
  video: 16 * MB,
  voice: 16 * MB,
};

export interface AttachmentSizeReading {
  amount: number;
  fractionDigits: 0 | 1;
  unit: "kilobyte" | "megabyte";
}

/** A size as the number and unit a host reads: whole KB, or MB to one place under 10. */
export function attachmentSizeReading(bytes: number): AttachmentSizeReading {
  if (bytes < MB) {
    return {
      amount: Math.max(1, Math.round(bytes / 1024)),
      fractionDigits: 0,
      unit: "kilobyte",
    };
  }
  const mb = bytes / MB;
  return { amount: mb, fractionDigits: mb >= 10 ? 0 : 1, unit: "megabyte" };
}

/**
 * "22 MB" — the size as a host would say it, not as bytes. A whole number of
 * megabytes loses the decimal, because every cap in the matrix is a whole
 * number and "WhatsApp accepts 5.0 MB" reads like a machine wrote it.
 */
export function formatAttachmentSize(bytes: number): string {
  const { amount, fractionDigits, unit } = attachmentSizeReading(bytes);
  const number = amount
    .toFixed(fractionDigits)
    .replace(TRAILING_ZERO_DECIMAL, "");
  return `${number} ${unit === "kilobyte" ? "KB" : "MB"}`;
}

/** The ceiling a transport puts on a kind, or null when it carries none. */
function maxBytesFor(
  transport: AttachmentTransport,
  kind: AttachmentKind,
  ctx: AttachmentCapabilityContext
): number | null {
  switch (transport) {
    case "whatsapp":
      return WHATSAPP_MAX_BYTES[kind];
    case "sms":
      return ctx.smsSupportsMms ? MMS_MAX_BYTES : null;
    case "email":
      return EMAIL_MAX_ATTACHMENT_BYTES;
    case "staff":
      return STAFF_MAX_BYTES[kind];
    default:
      // instagram / booking_com / airbnb: no verified media API.
      return null;
  }
}

/** What a host calls each kind. Exhaustive, so a new `MediaKind` won't compile. */
const NOUN_BY_KIND: Record<AttachmentKind, string> = {
  file: "file",
  image: "photo",
  video: "video",
  voice: "voice message",
};

/** What a host calls each channel. SMS only carries media as MMS. */
const CHANNEL_LABEL: Partial<Record<AttachmentTransport, string>> = {
  email: "Email",
  sms: "MMS",
  whatsapp: "WhatsApp",
};

/**
 * Only a guest conversation can be moved to another channel. A staff chat is
 * internal and has no email of its own, so pointing a colleague at "email"
 * names a door that isn't there; email itself has nowhere further to go.
 */
function hasEmailAlternative(transport: AttachmentTransport): boolean {
  return transport !== "email" && transport !== "staff";
}

/** Where a host should send this instead, named in the oversize copy. */
function oversizeReason(
  transport: AttachmentTransport,
  kind: AttachmentKind,
  sizeBytes: number,
  maxBytes: number
): string {
  const noun = NOUN_BY_KIND[kind];
  const channel = CHANNEL_LABEL[transport] ?? "This chat";
  const alternative = hasEmailAlternative(transport)
    ? " Send it by email instead."
    : "";
  return `This ${noun} is ${formatAttachmentSize(sizeBytes)}. ${channel} accepts ${formatAttachmentSize(maxBytes)}.${alternative}`;
}

const REFUSAL_SENTENCE: Record<
  Exclude<AttachmentRefusal["code"], "oversize">,
  string
> = {
  outside_service_window:
    "WhatsApp only allows attachments within 24 hours of the guest's last message. Send it by email, or send a template to reopen the conversation.",
  outside_service_window_voice:
    "WhatsApp only allows voice messages within 24 hours of the guest's last message. Send it by email instead.",
  sms_no_media:
    "SMS can't carry attachments. Switch this conversation to WhatsApp or email.",
  unsupported_channel: "This channel doesn't support attachments.",
};

/**
 * The server's English for a refusal, recorded on a message the dispatcher
 * would not send. The host app words the same codes from its own catalog.
 */
export function describeAttachmentRefusal(
  transport: AttachmentTransport,
  kind: AttachmentKind,
  refusal: AttachmentRefusal
): string {
  return refusal.code === "oversize"
    ? oversizeReason(transport, kind, refusal.sizeBytes, refusal.maxBytes)
    : REFUSAL_SENTENCE[refusal.code];
}

/**
 * Whether `kind` can be attached to a message on `transport`, and up to what
 * size. Pure: the composer calls it to decide what to enable, and the dispatcher
 * calls it again before handing bytes to a provider, so a client that ignores
 * the disabled state still cannot push media the transport would reject.
 *
 * Pass `sizeBytes` once a file is in hand to get the oversize verdict too; omit
 * it to ask only whether the control should be enabled at all.
 */
export function attachmentCapability(
  transport: AttachmentTransport,
  kind: AttachmentKind,
  ctx: AttachmentCapabilityContext = {}
): AttachmentCapability {
  const disabled = (
    code: Exclude<AttachmentRefusal["code"], "oversize">,
    requiresTemplate = false
  ): AttachmentCapability => ({
    enabled: false,
    maxBytes: null,
    reason: { code },
    requiresTemplate,
  });

  if (transport === "sms" && !ctx.smsSupportsMms) {
    return disabled("sms_no_media");
  }

  if (transport === "whatsapp" && !ctx.withinServiceWindow) {
    // Photos, video and documents CAN leave the window, but only inside an
    // approved media-header template. Voice has no template header at all, so
    // it is a flat no.
    return disabled(
      kind === "voice"
        ? "outside_service_window_voice"
        : "outside_service_window",
      kind !== "voice"
    );
  }

  const maxBytes = maxBytesFor(transport, kind, ctx);
  if (maxBytes === null) {
    return disabled("unsupported_channel");
  }

  if (ctx.sizeBytes !== undefined && ctx.sizeBytes > maxBytes) {
    return {
      enabled: false,
      maxBytes,
      reason: { code: "oversize", maxBytes, sizeBytes: ctx.sizeBytes },
      requiresTemplate: false,
    };
  }

  return { enabled: true, maxBytes, reason: null, requiresTemplate: false };
}
